import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import Layout from "./Layout";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeRaw from "rehype-raw";
import rehypeHighlight from "rehype-highlight";
import rehypeSlug from "rehype-slug";
import rehypeKatex from "rehype-katex";
import { ArrowLeft, Clock, Calendar, ArrowUp, Menu } from "lucide-react";
import { getPostBySlug } from "../data/blogPosts";

export default function BlogPost() {
  const { isDark } = useTheme();
  const { slug } = useParams();
  const [activeSection, setActiveSection] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [tocStructure, setTocStructure] = useState([]);
  const [expandedSections, setExpandedSections] = useState(new Set());

  // Scroll to top when blog post loads or slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Get post from data file
  const post = getPostBySlug(slug);

  // If post not found, show error
  if (!post) {
    return (
      <Layout>
        {({ toggleMobileMenu }) => (
          <div className="flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-12">
            <div className="w-full max-w-content">
              <div className="text-center py-16">
                <h1
                  className={`text-4xl font-display font-semibold mb-4 ${
                    isDark ? "text-white" : "text-text"
                  }`}
                >
                  Post Not Found
                </h1>
                <p
                  className={`text-base mb-6 ${
                    isDark ? "text-gray-400" : "text-text-muted"
                  }`}
                >
                  The blog post you're looking for doesn't exist.
                </p>
                <Link
                  to="/"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg border transition-all duration-base ${
                    isDark
                      ? "text-gray-400 border-border-dark hover:text-white hover:border-primary-dark/50 hover:bg-border-dark"
                      : "text-text-muted border-border/50 hover:text-text hover:border-primary/50 hover:bg-secondary/20"
                  }`}
                >
                  <ArrowLeft size={16} />
                  Back to all posts
                </Link>
              </div>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTOCClick = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Build TOC structure from rendered headings
  useEffect(() => {
    const timer = setTimeout(() => {
      const headings = document.querySelectorAll(
        "article h1[id], article h2[id], article h3[id], article h4[id], article h5[id]",
      );

      const structure = [];
      let currentH2 = null;
      let currentH3 = null;

      headings.forEach((heading) => {
        const level = heading.tagName.toLowerCase();
        const id = heading.id;
        const text = heading.textContent;

        if (level === "h1" || level === "h2") {
          const item = { id, text, level, children: [] };
          structure.push(item);
          if (level === "h2") {
            currentH2 = item;
            currentH3 = null;
          }
        } else if (level === "h3" && currentH2) {
          const h3Item = { id, text, level, children: [] };
          currentH2.children.push(h3Item);
          currentH3 = h3Item;
        } else if (level === "h4" && currentH3) {
          currentH3.children.push({ id, text, level, children: [] });
        } else if (
          level === "h5" &&
          currentH3 &&
          currentH3.children.length > 0
        ) {
          const lastH4 = currentH3.children[currentH3.children.length - 1];
          if (lastH4.children) {
            lastH4.children.push({ id, text, level });
          }
        }
      });

      setTocStructure(structure);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const headings = Array.from(
        document.querySelectorAll(
          "article h1[id], article h2[id], article h3[id], article h4[id], article h5[id]",
        ),
      );

      if (headings.length === 0) return;

      const scrollPosition = window.scrollY + 200;

      // Find the current heading based on scroll position
      let currentSection = "";
      let currentParentH2 = "";

      // Go through headings in reverse to find the one we've scrolled past
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        const headingTop = heading.offsetTop;

        if (scrollPosition >= headingTop) {
          currentSection = heading.id;
          break;
        }
      }

      // If we found a current section, find its parent H2
      if (currentSection) {
        const currentHeading = document.getElementById(currentSection);
        if (currentHeading) {
          const level = currentHeading.tagName.toLowerCase();

          if (level === "h2" || level === "h1") {
            currentParentH2 = currentSection;
          } else {
            // For h3, h4, h5, find the closest preceding h2
            for (let i = headings.length - 1; i >= 0; i--) {
              const heading = headings[i];
              if (
                heading.tagName.toLowerCase() === "h2" &&
                heading.offsetTop <= currentHeading.offsetTop
              ) {
                currentParentH2 = heading.id;
                break;
              }
            }
          }
        }
      }

      // Update active section if changed
      if (currentSection !== activeSection) {
        setActiveSection(currentSection);
      }

      // Auto-expand sections
      const newExpanded = new Set();

      // Expand the current parent H2 section
      if (currentParentH2) {
        newExpanded.add(currentParentH2);
      }

      // Also expand any section that contains visible headings
      tocStructure.forEach((item) => {
        if (item.children && item.children.length > 0) {
          const headingElement = document.getElementById(item.id);
          if (!headingElement) return;

          // Get the last child at any nesting level
          const getLastChild = (children) => {
            if (!children || children.length === 0) return null;
            const lastChild = children[children.length - 1];
            if (lastChild.children && lastChild.children.length > 0) {
              return getLastChild(lastChild.children);
            }
            return lastChild;
          };

          const lastChild = getLastChild(item.children);
          const lastChildElement = lastChild
            ? document.getElementById(lastChild.id)
            : null;

          if (lastChildElement) {
            const sectionStart = headingElement.offsetTop - 300;
            const sectionEnd =
              lastChildElement.offsetTop + lastChildElement.offsetHeight + 300;

            if (
              scrollPosition >= sectionStart &&
              scrollPosition <= sectionEnd
            ) {
              newExpanded.add(item.id);
            }
          }
        }
      });

      setExpandedSections(newExpanded);
    };

    // Debounce scroll handler
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    handleScroll(); // Initial call
    return () => window.removeEventListener("scroll", onScroll);
  }, [activeSection, tocStructure]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Show button when past 50% of the page
      const scrollPercentage = scrollPosition / (documentHeight - windowHeight);
      setShowScrollTop(scrollPercentage > 0.5);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Custom right sidebar with Dynamic Table of Contents
  const TableOfContents = () => (
    <aside
      className={`w-72 border-l overflow-y-auto hide-scrollbar min-h-screen sticky top-0 h-screen hidden xl:block ${
        isDark ? "border-border-dark bg-background-dark" : "border-border/50"
      }`}
    >
      <div className="p-6">
        <div>
          <h3
            className={`text-sm font-display font-semibold uppercase tracking-wider mb-4 ${
              isDark ? "text-white" : "text-text"
            }`}
          >
            Table of Contents
          </h3>
          <nav className="space-y-1">
            {tocStructure.map((item) => (
              <div key={item.id}>
                {/* Main heading (h1/h2) */}
                <a
                  href={`#${item.id}`}
                  onClick={(e) => handleTOCClick(e, item.id)}
                  className={`text-sm transition-all duration-200 leading-snug block pl-3 border-l-2 py-1.5 ${
                    activeSection === item.id
                      ? isDark
                        ? "text-primary-dark border-primary-dark font-medium"
                        : "text-primary border-primary font-medium"
                      : isDark
                        ? "text-gray-400 hover:text-primary-dark border-transparent hover:border-primary-dark"
                        : "text-text-muted hover:text-primary border-transparent hover:border-primary"
                  }`}
                >
                  {item.text}
                </a>

                {/* Sub-headings (h3, h4, h5) with smooth expand/collapse */}
                {item.children && item.children.length > 0 && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      expandedSections.has(item.id)
                        ? "max-h-[600px] opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="ml-3 mt-1 space-y-1 border-l border-gray-300 dark:border-gray-700 pl-3">
                      {item.children.map((child) => (
                        <div key={child.id}>
                          {/* H3 level */}
                          <a
                            href={`#${child.id}`}
                            onClick={(e) => handleTOCClick(e, child.id)}
                            className={`text-xs transition-all duration-200 leading-snug block py-1 ${
                              activeSection === child.id
                                ? isDark
                                  ? "text-primary-dark font-medium"
                                  : "text-primary font-medium"
                                : isDark
                                  ? "text-gray-500 hover:text-primary-dark"
                                  : "text-text-light hover:text-primary"
                            }`}
                          >
                            {child.text}
                          </a>

                          {/* H4 level */}
                          {child.children && child.children.length > 0 && (
                            <div className="ml-3 mt-1 space-y-1 border-l border-gray-400 dark:border-gray-600 pl-2">
                              {child.children.map((h4) => (
                                <div key={h4.id}>
                                  <a
                                    href={`#${h4.id}`}
                                    onClick={(e) => handleTOCClick(e, h4.id)}
                                    className={`text-xs transition-all duration-200 leading-snug block py-0.5 ${
                                      activeSection === h4.id
                                        ? isDark
                                          ? "text-primary-dark font-medium"
                                          : "text-primary font-medium"
                                        : isDark
                                          ? "text-gray-600 hover:text-primary-dark"
                                          : "text-text-light hover:text-primary"
                                    }`}
                                  >
                                    {h4.text}
                                  </a>

                                  {/* H5 level */}
                                  {h4.children && h4.children.length > 0 && (
                                    <div className="ml-2 mt-0.5 space-y-0.5 border-l border-gray-500 dark:border-gray-500 pl-2">
                                      {h4.children.map((h5) => (
                                        <a
                                          key={h5.id}
                                          href={`#${h5.id}`}
                                          onClick={(e) =>
                                            handleTOCClick(e, h5.id)
                                          }
                                          className={`text-xs transition-all duration-200 leading-snug block py-0.5 ${
                                            activeSection === h5.id
                                              ? isDark
                                                ? "text-primary-dark font-medium"
                                                : "text-primary font-medium"
                                              : isDark
                                                ? "text-gray-600 hover:text-primary-dark"
                                                : "text-text-light hover:text-primary"
                                          }`}
                                        >
                                          {h5.text}
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </aside>
  );

  return (
    <Layout rightSidebar={<TableOfContents />} showFloatingMenu={true}>
      {({ toggleMobileMenu }) => (
        <div className="flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-12">
          <div className="w-full max-w-content">
            {/* Back Button */}
            <Link
              to="/"
              className={`inline-flex items-center gap-2 mb-8 transition-colors duration-base ${
                isDark
                  ? "text-gray-400 hover:text-primary-dark"
                  : "text-text-muted hover:text-primary"
              }`}
            >
              <ArrowLeft size={20} />
              <span className="text-sm">Back to all posts</span>
            </Link>

            {/* Article */}
            <article>
              {/* Article Header */}
              <header className="mb-12">
                {/* Tags at top - max 3 */}
                <div className="flex gap-2 mb-1 -mt-2">
                  {post.tags.slice(0, 3).map((tag, index) => (
                    <Link
                      key={index}
                      to={`/tags/${tag.toLowerCase()}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border transition-all duration-base ${
                        isDark
                          ? "bg-background-dark text-gray-400 border-border-dark hover:border-primary-dark/50 hover:bg-border-dark"
                          : "bg-background-sidebar text-text-muted border-border/50 hover:border-primary/50 hover:bg-secondary/20"
                      }`}
                    >
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${isDark ? "bg-primary-dark" : "bg-primary"}`}
                      ></div>
                      {tag}
                    </Link>
                  ))}
                </div>

                {/* Title */}
                <h1
                  className={`text-4xl font-display font-semibold mb-4 tracking-tight leading-tight -mt-1 ${
                    isDark ? "text-white" : "text-text"
                  }`}
                >
                  {post.title}
                </h1>

                {/* Excerpt */}
                <p
                  className={`text-base leading-normal mb-6 ${
                    isDark ? "text-gray-400" : "text-text-muted"
                  }`}
                >
                  {post.excerpt}
                </p>

                {/* Date and Read Time - Bottom right */}
                <div className="flex justify-end">
                  <div
                    className={`flex items-center gap-4 text-sm ${
                      isDark ? "text-gray-400" : "text-text-muted"
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-1 h-1 rounded-full ${
                          isDark ? "bg-gray-400" : "bg-text-muted"
                        }`}
                      ></div>
                      <time dateTime={post.date}>
                        {new Date(post.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </time>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1.5">
                      <Clock size={14} />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>
              </header>

              {/* Article Content */}
              <div className="blog-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[
                    rehypeRaw,
                    rehypeHighlight,
                    rehypeSlug,
                    rehypeKatex,
                  ]}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              {/* Scroll to Top Button - Only show when scrolled past 50% */}
              {showScrollTop && (
                <div className="flex justify-end mt-12">
                  <button
                    onClick={scrollToTop}
                    className={`p-3 rounded-full shadow-lg transition-all duration-300 ${
                      isDark
                        ? "bg-gray-800 text-white border border-gray-700 hover:bg-gray-700"
                        : "bg-white text-text border border-border hover:bg-secondary/20"
                    } animate-in`}
                    aria-label="Scroll to top"
                  >
                    <ArrowUp size={20} />
                  </button>
                </div>
              )}
            </article>
          </div>
        </div>
      )}
    </Layout>
  );
}
